import { useEffect, useState, useMemo } from 'react'
import { api } from '../api'
import {
  Card, Button, Form, Input, Select, DatePicker, Tag, Empty, Spin, Space, Typography,
  Drawer, Checkbox, InputNumber, Modal, message, Popconfirm, Radio, Divider, Segmented, Alert
} from 'antd'
import {
  PlusOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CalendarOutlined, ExperimentOutlined, ThunderboltOutlined, ToolOutlined, UndoOutlined,
  WarningOutlined, CopyOutlined, ShoppingCartOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography
const { TextArea } = Input

const UNIDADES = ['L', 'cl', 'ml', 'kg', 'g']

const MAPA_UNIDAD_INV = {
  litros: 'L', litro: 'L', l: 'L',
  ml: 'ml', cl: 'cl',
  gramos: 'g', gramo: 'g', g: 'g',
  kg: 'kg', kilos: 'kg', kilo: 'kg', kilogramos: 'kg',
}

const normalizarUnidad = (u) => {
  if (!u) return 'L'
  if (UNIDADES.includes(u)) return u
  return MAPA_UNIDAD_INV[String(u).toLowerCase()] || 'L'
}

const COLOR_ESTADO = {
  pendiente: 'processing',
  aplicado: 'success',
  cancelado: 'default',
}

const LABEL_ESTADO = {
  pendiente: 'Pendiente',
  aplicado: 'Aplicado',
  cancelado: 'Cancelado',
}

export default function Tratamientos({ usuario }) {
  const isMobile = useIsMobile()
  const [registros, setRegistros] = useState([])
  const [cultivos, setCultivos] = useState([])
  const [productos, setProductos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [filtro, setFiltro] = useState('pendiente')
  const [form] = Form.useForm()
  const modoActual = Form.useWatch('modo', form)

  // Productos seleccionados (mezcla inventario + manuales) en el formulario
  const [prodsForm, setProdsForm] = useState([])

  // Modal de productos a pedir tras guardar
  const [pedido, setPedido] = useState(null) // { items: [...], fecha, modo, cultivo }

  // Devuelve la información de stock para un producto del formulario
  const stockInfo = (p) => {
    if (p.es_manual) return { faltante: true, motivo: 'no en inventario', stock: null }
    const inv = productos.find(x => x.id === p.producto_id)
    if (!inv) return { faltante: true, motivo: 'no en inventario', stock: null }
    const stock = parseFloat(inv.stock_actual ?? 0)
    const cant = parseFloat(p.cantidad || 0)
    if (cant > stock) return { faltante: true, motivo: 'stock insuficiente', stock, unidad_inv: inv.unidad_medida }
    return { faltante: false, stock, unidad_inv: inv.unidad_medida }
  }

  const calcularPedido = (prods) => {
    return prods
      .filter(p => (!p.es_manual && p.producto_id) || (p.es_manual && p.producto_nombre?.trim()))
      .map(p => {
        const info = stockInfo(p)
        return info.faltante ? {
          nombre: (p.producto_nombre || '').trim(),
          cantidad: parseFloat(p.cantidad),
          unidad: p.unidad,
          stock: info.stock,
          motivo: info.motivo,
        } : null
      })
      .filter(Boolean)
  }

  // Aplicar tratamiento
  const [aplicando, setAplicando] = useState(null)
  const [numUnidades, setNumUnidades] = useState(1)
  const [fechaApl, setFechaApl] = useState(dayjs())
  const [enviandoApl, setEnviandoApl] = useState(false)

  const puedeEditar = usuario?.rol === 'admin' || usuario?.rol === 'tecnico'
  const puedeBorrar = usuario?.rol === 'admin'

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    try {
      const [trats, cults, prods] = await Promise.all([
        api('/tratamientos-plan'),
        api('/cultivos'),
        api('/productos').catch(() => []),
      ])
      setRegistros(trats)
      setCultivos(cults)
      setProductos(prods)
    } catch (err) {
      console.error('Error cargando tratamientos:', err)
      message.error('Error cargando datos')
    }
    setCargando(false)
  }

  const filtrados = useMemo(() => {
    if (filtro === 'todos') return registros
    return registros.filter(r => r.estado === filtro)
  }, [registros, filtro])

  const abrirNuevo = () => {
    setEditando(null)
    form.resetFields()
    form.setFieldsValue({ fecha_prevista: dayjs(), modo: 'manual' })
    setProdsForm([])
    setMostrarForm(true)
  }

  const abrirEdicion = (t) => {
    setEditando(t)
    form.setFieldsValue({
      fecha_prevista: dayjs(t.fecha_prevista),
      modo: t.modo,
      cultivo_id: t.cultivo_id,
      notas: t.notas,
    })
    setProdsForm((t.productos || []).map(p => ({
      uid: p.id || `tmp-${Math.random()}`,
      producto_id: p.producto_id,
      producto_nombre: p.producto_nombre,
      es_manual: !p.producto_id,
      cantidad: p.cantidad,
      unidad: normalizarUnidad(p.unidad),
    })))
    setMostrarForm(true)
  }

  const cerrarForm = () => {
    setMostrarForm(false)
    setEditando(null)
    form.resetFields()
    setProdsForm([])
  }

  const toggleProductoInv = (prod, checked) => {
    if (checked) {
      setProdsForm([...prodsForm, {
        uid: `inv-${prod.id}`,
        producto_id: prod.id,
        producto_nombre: prod.nombre,
        es_manual: false,
        cantidad: 1,
        unidad: normalizarUnidad(prod.unidad_medida),
      }])
    } else {
      setProdsForm(prodsForm.filter(p => p.producto_id !== prod.id))
    }
  }

  const addProductoManual = () => {
    setProdsForm([...prodsForm, {
      uid: `man-${Date.now()}`,
      producto_id: null,
      producto_nombre: '',
      es_manual: true,
      cantidad: 1,
      unidad: 'L',
    }])
  }

  const updateProd = (uid, campo, valor) => {
    setProdsForm(prodsForm.map(p => p.uid === uid ? { ...p, [campo]: valor } : p))
  }

  const removeProd = (uid) => {
    setProdsForm(prodsForm.filter(p => p.uid !== uid))
  }

  const guardar = async (values) => {
    if (prodsForm.length === 0) {
      message.error('Añade al menos un producto')
      return
    }
    for (const p of prodsForm) {
      if (p.es_manual && !p.producto_nombre?.trim()) {
        message.error('Indica el nombre de los productos manuales')
        return
      }
      if (!p.cantidad || p.cantidad <= 0) {
        message.error('Cantidad inválida en alguno de los productos')
        return
      }
    }

    setGuardando(true)
    try {
      const payload = {
        fecha_prevista: values.fecha_prevista.format('YYYY-MM-DD'),
        modo: values.modo,
        cultivo_id: values.cultivo_id || null,
        notas: values.notas || null,
        productos: prodsForm.map(p => ({
          producto_id: p.es_manual ? null : p.producto_id,
          producto_nombre: p.es_manual ? p.producto_nombre.trim() : null,
          cantidad: parseFloat(p.cantidad),
          unidad: p.unidad,
        })),
      }
      if (editando) {
        await api(`/tratamientos-plan/${editando.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        message.success('Tratamiento actualizado')
      } else {
        await api('/tratamientos-plan', { method: 'POST', body: JSON.stringify(payload) })
        message.success('Tratamiento creado')
      }

      const items = calcularPedido(prodsForm)
      const cultivoNombre = cultivos.find(c => c.id === values.cultivo_id)?.nombre || null

      cerrarForm()
      cargar()

      if (items.length > 0) {
        setPedido({
          items,
          fecha: values.fecha_prevista.format('DD/MM/YYYY'),
          modo: values.modo,
          cultivo: cultivoNombre,
        })
      }
    } catch (err) {
      message.error(err.message || 'Error guardando')
    }
    setGuardando(false)
  }

  const textoPedido = (p) => {
    if (!p) return ''
    const lineas = [
      `Pedido tratamiento — ${p.fecha}${p.cultivo ? ' · ' + p.cultivo : ''}`,
      `Cantidades por ${p.modo === 'canon' ? 'cuba (1000 L)' : 'mochila'}:`,
      ...p.items.map(it => `- ${it.cantidad} ${it.unidad} de ${it.nombre}${it.motivo === 'no en inventario' ? ' (nuevo)' : ''}`),
    ]
    return lineas.join('\n')
  }

  const copiarPedido = async () => {
    try {
      await navigator.clipboard.writeText(textoPedido(pedido))
      message.success('Pedido copiado al portapapeles')
    } catch {
      message.error('No se pudo copiar')
    }
  }

  const eliminar = async (id) => {
    try {
      await api(`/tratamientos-plan/${id}`, { method: 'DELETE' })
      message.success('Tratamiento eliminado')
      cargar()
    } catch (err) {
      message.error(err.message || 'Error eliminando')
    }
  }

  const cancelar = async (id) => {
    try {
      await api(`/tratamientos-plan/${id}/cancelar`, { method: 'POST' })
      message.success('Tratamiento cancelado')
      cargar()
    } catch (err) {
      message.error(err.message || 'Error cancelando')
    }
  }

  const revertir = async (id) => {
    try {
      await api(`/tratamientos-plan/${id}/revertir`, { method: 'POST' })
      message.success('Aplicación revertida — stock repuesto')
      cargar()
    } catch (err) {
      message.error(err.message || 'Error revirtiendo')
    }
  }

  const abrirAplicar = (t) => {
    setAplicando(t)
    setNumUnidades(1)
    setFechaApl(dayjs())
  }

  const cerrarAplicar = () => {
    setAplicando(null)
  }

  const aplicar = async () => {
    if (!numUnidades || numUnidades <= 0) {
      message.error('Indica el número de unidades aplicadas')
      return
    }
    setEnviandoApl(true)
    try {
      await api(`/tratamientos-plan/${aplicando.id}/aplicar`, {
        method: 'POST',
        body: JSON.stringify({
          num_unidades: parseFloat(numUnidades),
          fecha_aplicacion: fechaApl.format('YYYY-MM-DD'),
        }),
      })
      message.success('Tratamiento aplicado y registrado en el cuaderno')
      cerrarAplicar()
      cargar()
    } catch (err) {
      message.error(err.message || 'Error aplicando tratamiento')
    }
    setEnviandoApl(false)
  }

  const labelUnidad = (modo) => modo === 'canon' ? 'cuba 1000L' : 'mochila'

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Tratamientos</Title>
        {puedeEditar && (
          <Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo} style={{ background: '#2E7D32', flexShrink: 0 }}>
            {isMobile ? '' : 'Nuevo'}
          </Button>
        )}
      </div>

      <div style={{ padding: '12px 16px 0' }}>
        <Segmented
          block
          value={filtro}
          onChange={setFiltro}
          options={[
            { label: 'Pendientes', value: 'pendiente' },
            { label: 'Aplicados', value: 'aplicado' },
            { label: 'Todos', value: 'todos' },
          ]}
        />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}><Spin size="large" /></div>
        ) : filtrados.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">No hay tratamientos {filtro !== 'todos' ? `en estado "${LABEL_ESTADO[filtro]?.toLowerCase()}"` : ''}</Text>}
            style={{ marginTop: '60px' }}
          />
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {filtrados.map((t) => (
              <Card key={t.id} size="small" style={{
                borderRadius: '12px',
                borderLeft: `3px solid ${t.estado === 'pendiente' ? '#1890ff' : t.estado === 'aplicado' ? '#52c41a' : '#bfbfbf'}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <Tag color={COLOR_ESTADO[t.estado]} style={{ margin: 0 }}>{LABEL_ESTADO[t.estado]}</Tag>
                      <Tag color={t.modo === 'canon' ? 'volcano' : 'cyan'} icon={t.modo === 'canon' ? <ThunderboltOutlined /> : <ToolOutlined />} style={{ margin: 0 }}>
                        {t.modo === 'canon' ? 'Cañón' : 'Manual'}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <CalendarOutlined /> {dayjs(t.fecha_prevista).format('DD/MM/YYYY')}
                      </Text>
                    </div>
                    {t.cultivo_nombre && (
                      <Text style={{ fontSize: '13px', display: 'block' }}>
                        <ExperimentOutlined /> {t.cultivo_nombre}
                      </Text>
                    )}
                    {t.productos && t.productos.length > 0 && (
                      <div style={{ marginTop: '6px' }}>
                        {t.productos.map((p, i) => (
                          <div key={i} style={{ fontSize: '12px', color: '#555' }}>
                            • {p.producto_nombre} — <strong>{p.cantidad} {p.unidad}</strong>
                            <Text type="secondary" style={{ fontSize: '11px', marginLeft: '4px' }}>
                              / {labelUnidad(t.modo)}
                            </Text>
                            {p.es_manual && <Tag color="orange" style={{ marginLeft: '4px', fontSize: '10px' }}>manual</Tag>}
                          </div>
                        ))}
                      </div>
                    )}
                    {t.estado === 'aplicado' && (
                      <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                        Aplicado el {dayjs(t.fecha_aplicacion).format('DD/MM/YYYY')} • {t.num_unidades} {t.modo === 'canon' ? 'cubas' : 'mochilas'}
                        {t.aplicado_por_nombre && ` • ${t.aplicado_por_nombre}`}
                      </Text>
                    )}
                    {t.notas && (
                      <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                        {t.notas}
                      </Text>
                    )}
                    {t.creado_por_nombre && (
                      <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginTop: '4px' }}>
                        Creado por {t.creado_por_nombre}
                      </Text>
                    )}
                  </div>
                  <Space direction="vertical" size={2} style={{ flexShrink: 0 }}>
                    {t.estado === 'pendiente' && puedeEditar && (
                      <>
                        <Button type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => abrirAplicar(t)} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                          Aplicar
                        </Button>
                        <Button size="small" icon={<EditOutlined />} onClick={() => abrirEdicion(t)} />
                        <Popconfirm
                          title="¿Cancelar este tratamiento?"
                          description="Los productos reservados seguirán fuera del inventario. Para devolverlos, elimina el tratamiento."
                          onConfirm={() => cancelar(t.id)}
                          okText="Sí"
                          cancelText="No"
                        >
                          <Button size="small" icon={<CloseCircleOutlined />} />
                        </Popconfirm>
                      </>
                    )}
                    {t.estado === 'aplicado' && puedeEditar && (
                      <Popconfirm
                        title="¿Revertir esta aplicación?"
                        description="Se eliminará del cuaderno y se devolverá el stock al inventario."
                        onConfirm={() => revertir(t.id)}
                        okText="Sí, revertir"
                        cancelText="No"
                      >
                        <Button size="small" icon={<UndoOutlined />}>Revertir</Button>
                      </Popconfirm>
                    )}
                    {puedeBorrar && (
                      <Popconfirm
                        title="¿Eliminar definitivamente?"
                        description={t.estado === 'aplicado'
                          ? 'Se borrará también del cuaderno y se devolverá el stock al inventario.'
                          : 'Esta acción no se puede deshacer.'}
                        onConfirm={() => eliminar(t.id)}
                        okText="Sí, eliminar"
                        okButtonProps={{ danger: true }}
                        cancelText="No"
                      >
                        <Button danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <Drawer
        title={editando ? 'Editar tratamiento' : 'Nuevo tratamiento'}
        open={mostrarForm}
        onClose={cerrarForm}
        width={isMobile ? '100%' : 420}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={cerrarForm}>Cancelar</Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              {editando ? 'Actualizar' : 'Crear'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item label="Fecha prevista" name="fecha_prevista" rules={[{ required: true, message: 'Obligatorio' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item label="Modo de aplicación" name="modo" rules={[{ required: true }]}>
            <Radio.Group buttonStyle="solid">
              <Radio.Button value="manual"><ToolOutlined /> Manual (mochila)</Radio.Button>
              <Radio.Button value="canon"><ThunderboltOutlined /> Cañón (cuba 1000L)</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item label="Cultivo" name="cultivo_id">
            <Select placeholder="Selecciona un cultivo (opcional)" allowClear>
              {cultivos.map(c => (
                <Select.Option key={c.id} value={c.id}>{c.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Notas" name="notas">
            <TextArea rows={2} placeholder="Observaciones..." />
          </Form.Item>

          <Divider style={{ margin: '12px 0' }} />

          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong>Productos</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              cantidad / {modoActual === 'canon' ? 'cuba 1000L' : 'mochila'}
            </Text>
          </div>

          {prodsForm.some(p => stockInfo(p).faltante) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: '10px' }}
              message="Hay productos a pedir"
              description="Algunos productos no están en inventario o no hay stock suficiente. Al guardar verás la lista para copiar y enviar el pedido."
            />
          )}

          {prodsForm.length > 0 && (
            <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: '12px' }}>
              {prodsForm.map(p => {
                const info = stockInfo(p)
                return (
                <Card
                  key={p.uid}
                  size="small"
                  style={{
                    borderRadius: '8px',
                    background: p.es_manual ? '#fff7e6' : '#f6ffed',
                    borderColor: info.faltante ? '#ff4d4f' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {p.es_manual ? (
                      <Input
                        size="small"
                        placeholder="Nombre del producto"
                        value={p.producto_nombre}
                        onChange={e => updateProd(p.uid, 'producto_nombre', e.target.value)}
                        style={{ flex: 1 }}
                      />
                    ) : (
                      <Text style={{ fontSize: '13px', flex: 1 }}>{p.producto_nombre}</Text>
                    )}
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeProd(p.uid)} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                    <InputNumber
                      size="small"
                      min={0.001}
                      step={0.5}
                      value={p.cantidad}
                      onChange={v => updateProd(p.uid, 'cantidad', v)}
                      style={{ flex: 1 }}
                    />
                    <Select
                      size="small"
                      value={p.unidad}
                      onChange={v => updateProd(p.uid, 'unidad', v)}
                      style={{ width: 80 }}
                    >
                      {UNIDADES.map(u => <Select.Option key={u} value={u}>{u}</Select.Option>)}
                    </Select>
                  </div>
                  {info.faltante && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#cf1322', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <WarningOutlined />
                      {info.motivo === 'no en inventario'
                        ? 'No está en inventario — habrá que pedirlo'
                        : `Stock insuficiente (disponible ${info.stock} ${info.unidad_inv || ''})`}
                    </div>
                  )}
                  {!info.faltante && !p.es_manual && info.stock != null && (
                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#52c41a' }}>
                      Stock disponible: {info.stock} {info.unidad_inv || ''}
                    </div>
                  )}
                </Card>
              )})}
            </Space>
          )}

          <Button block icon={<PlusOutlined />} onClick={addProductoManual} style={{ marginBottom: '12px' }}>
            Añadir producto a mano
          </Button>

          {productos.length > 0 && (
            <>
              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Del inventario:</Text>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {productos.map(prod => {
                  const seleccionado = prodsForm.find(p => p.producto_id === prod.id)
                  return (
                    <Card key={prod.id} size="small" style={{ borderRadius: '6px' }}>
                      <Checkbox
                        checked={!!seleccionado}
                        onChange={(e) => toggleProductoInv(prod, e.target.checked)}
                      >
                        <Text style={{ fontSize: '12px' }}>{prod.nombre}</Text>
                        <Text type="secondary" style={{ fontSize: '10px', marginLeft: '6px' }}>
                          (Stock: {prod.stock_actual ?? 0} {prod.unidad_medida})
                        </Text>
                      </Checkbox>
                    </Card>
                  )
                })}
              </Space>
            </>
          )}
        </Form>
      </Drawer>

      <Modal
        open={!!aplicando}
        title="Aplicar tratamiento"
        onCancel={cerrarAplicar}
        onOk={aplicar}
        okText="Confirmar aplicación"
        confirmLoading={enviandoApl}
        cancelText="Cancelar"
      >
        {aplicando && (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
              {aplicando.modo === 'canon' ? 'Modo cañón (cuba 1000L)' : 'Modo manual (mochila)'}
              {aplicando.cultivo_nombre && ` — ${aplicando.cultivo_nombre}`}
            </Text>

            <div style={{ marginBottom: '12px' }}>
              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Nº de {aplicando.modo === 'canon' ? 'cubas' : 'mochilas'} aplicadas:
              </Text>
              <InputNumber
                min={0.5}
                step={1}
                value={numUnidades}
                onChange={setNumUnidades}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Fecha de aplicación:</Text>
              <DatePicker value={fechaApl} onChange={setFechaApl} style={{ width: '100%' }} format="DD/MM/YYYY" />
            </div>

            <Divider style={{ margin: '8px 0' }} />
            <Text strong style={{ fontSize: '12px' }}>Total a consumir:</Text>
            <div style={{ marginTop: '4px' }}>
              {(aplicando.productos || []).map((p, i) => (
                <div key={i} style={{ fontSize: '12px' }}>
                  • {p.producto_nombre}: <strong>{(parseFloat(p.cantidad) * parseFloat(numUnidades || 0)).toFixed(2)} {p.unidad}</strong>
                  {!p.es_manual && p.stock_actual != null && (
                    <Text type="secondary" style={{ fontSize: '10px', marginLeft: '6px' }}>
                      (stock actual: {p.stock_actual})
                    </Text>
                  )}
                </div>
              ))}
            </div>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '8px' }}>
              Se descontará del inventario y se registrará en el cuaderno de campo.
            </Text>
          </div>
        )}
      </Modal>

      <Modal
        open={!!pedido}
        title={<Space><ShoppingCartOutlined />Productos a pedir</Space>}
        onCancel={() => setPedido(null)}
        footer={
          <Space>
            <Button onClick={() => setPedido(null)}>Cerrar</Button>
            <Button type="primary" icon={<CopyOutlined />} onClick={copiarPedido}>Copiar pedido</Button>
          </Space>
        }
        width={isMobile ? '95%' : 480}
      >
        {pedido && (
          <div>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: '12px' }}
              message="Falta stock o son productos nuevos"
              description="Copia el pedido y envíalo para reponer antes de aplicar el tratamiento. Las cantidades son por mochila/cuba; multiplícalas por el número de unidades que vayas a aplicar."
            />
            <div style={{
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              marginBottom: '8px',
            }}>
              {textoPedido(pedido)}
            </div>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {pedido.items.length} producto{pedido.items.length === 1 ? '' : 's'} en el pedido.
            </Text>
          </div>
        )}
      </Modal>
    </div>
  )
}
